import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import repositoryContainer from '../../../services/RepositoryContainer';
import { useAuth } from '../../../components/context/AuthContext';

const ClientExperienceSuite = () => {
  const { user, setUser } = useAuth();

  const [formData, setFormData] = useState({
    arrivalPolicy: '',
    cancellationPolicy: '',
    preparationInstructions: '',
    acceptedPaymentMethods: [],
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const paymentMethodOptions = [
    { value: 'Credit Card', label: 'Credit Card', icon: 'CreditCard' },
    { value: 'Debit Card', label: 'Debit Card', icon: 'CreditCard' },
    { value: 'Cash', label: 'Cash', icon: 'Banknote' },
    { value: 'Bank Transfer', label: 'Bank Transfer', icon: 'Building' },
    { value: 'Mobile Money', label: 'Mobile Money', icon: 'Smartphone' },
    { value: 'Digital Wallet', label: 'Digital Wallet', icon: 'Wallet' },
  ];

  /**
   * Populate all fields (Textareas + Checkboxes) from the Auth Context
   * This runs on mount and whenever the global 'user' object updates.
   */
  useEffect(() => {
    const company = user?.company;

    if (company) {
      setFormData({
        // Populating text fields from user.company
        arrivalPolicy: company.arrivalPolicy || '',
        cancellationPolicy: company.cancellationPolicy || '',
        preparationInstructions: company.preparationInstructions || '',
        
        // Populating checkboxes by splitting the comma-separated string
        acceptedPaymentMethods: company.acceptedPaymentMethods 
          ? company.acceptedPaymentMethods.split(', ') 
          : [],
      });
      // Reset changes flag since we just synced with the server data
      setHasChanges(false);
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setHasChanges(true);
    setMessage(null);
  };

  const handlePaymentMethodToggle = (methodValue) => {
    setFormData((prev) => {
      const current = prev.acceptedPaymentMethods || [];
      const updated = current.includes(methodValue)
        ? current.filter((m) => m !== methodValue)
        : [...current, methodValue];
      return { ...prev, acceptedPaymentMethods: updated };
    });
    setHasChanges(true);
    setMessage(null);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    try {
      setLoading(true);
      setMessage(null);

      const payload = {
        arrivalPolicy: formData.arrivalPolicy,
        cancellationPolicy: formData.cancellationPolicy,
        preparationInstructions: formData.preparationInstructions,
        acceptedPaymentMethods: formData.acceptedPaymentMethods.join(', '), 
      };

      await repositoryContainer.company.updatePolicies(payload);

      if (typeof setUser === 'function') {
        setUser((prev) => ({
          ...prev,
          company: {
            ...prev.company,
            ...payload,
          },
        }));
      }

      setHasChanges(false);
      setMessage({ type: 'success', text: 'Operational protocols updated successfully!' });
      setTimeout(() => setMessage(null), 5000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || 'Failed to update protocols',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const company = user?.company;
    if (company) {
      setFormData({
        arrivalPolicy: company.arrivalPolicy || '',
        cancellationPolicy: company.cancellationPolicy || '',
        preparationInstructions: company.preparationInstructions || '',
        acceptedPaymentMethods: company.acceptedPaymentMethods ? company.acceptedPaymentMethods.split(', ') : [],
      });
    }
    setHasChanges(false);
    setMessage(null);
  };

  return (
    <div className="settings-bento-card">
      <div className="settings-card-header">
        <h3 className="settings-card-title">Client Protocols</h3>
        <p className="settings-card-description">Define the expectations and rules for your clients.</p>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg border animate-in fade-in zoom-in-95 duration-200 ${
          message.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <p className="text-sm font-medium">{message.text}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div>
            <label className="text-sm font-bold block mb-3">Arrival Policy</label>
            <textarea
              name="arrivalPolicy"
              className="settings-textarea w-full"
              value={formData.arrivalPolicy}
              onChange={handleInputChange}
              placeholder="Describe late arrival policies..."
            />
          </div>

          <div>
            <label className="text-sm font-bold block mb-3">Cancellation Policy</label>
            <textarea
              name="cancellationPolicy"
              className="settings-textarea w-full"
              value={formData.cancellationPolicy}
              onChange={handleInputChange}
              placeholder="Outline cancellation and no-show rules..."
            />
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="text-sm font-bold block mb-3">Preparation Instructions</label>
            <textarea
              name="preparationInstructions"
              className="settings-textarea w-full"
              style={{ minHeight: '120px' }}
              value={formData.preparationInstructions}
              onChange={handleInputChange}
              placeholder="Instructions for clients before appointment..."
            />
          </div>

          <div>
            <label className="text-sm font-bold block mb-3">Accepted Payment Methods</label>
            <div className="grid grid-cols-2 gap-2">
              {paymentMethodOptions.map((method) => {
                const isSelected = formData.acceptedPaymentMethods?.includes(method.value);
                return (
                  <div
                    key={method.value}
                    onClick={() => handlePaymentMethodToggle(method.value)}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border-2 transition-all cursor-pointer ${
                      isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-border/30 bg-muted/30 hover:border-border/50'
                    }`}
                  >
                    <Checkbox checked={isSelected} readOnly />
                    <Icon name={method.icon} size={16} className="text-primary flex-shrink-0" />
                    <span className="text-xs font-medium truncate">{method.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-6 border-t border-border/50 mt-6">
        <Button variant="outline" onClick={handleCancel} disabled={!hasChanges || loading}>Cancel</Button>
        <Button variant="default" onClick={handleSubmit} disabled={!hasChanges || loading}>
          {loading ? 'Saving...' : 'Update Protocols'}
        </Button>
      </div>
    </div>
  );
};

export default ClientExperienceSuite;